import React, { useCallback, useEffect, useState } from "react";
import { DataTable } from "../common/DataTable";
import { StatusBadge } from "../common/StatusBadge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Pick } from "../loans/LoanFormDialog";
import { Reminders } from "../../lib/api";
import { formatIndianDate } from "../../lib/format";
import { TYPES, TYPE_LABEL } from "./constants";
import { toast } from "sonner";
import { Eye, Send, RotateCcw, Ban } from "lucide-react";

const STATUSES = ["Queued", "Sent", "Delivered", "Failed", "Cancelled"];
const rs = (p) => `₹${(p / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const Details = ({ r, onClose }) => (
  <Dialog open onOpenChange={onClose}>
    <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto bg-white" data-testid="reminder-details">
      <DialogHeader><DialogTitle className="font-display text-emerald-950">{r.loan_number} · {TYPE_LABEL[r.type]}</DialogTitle>
        <DialogDescription>{r.recipient_name} · {r.phone_masked} · {r.language === "bn" ? "বাংলা" : "English"}</DialogDescription></DialogHeader>
      <div className="whitespace-pre-wrap rounded-xl bg-[#E7F7EF] p-3 text-sm">{r.message}</div>
      <div className="text-xs text-emerald-900/70">
        <div className="mb-1 font-semibold uppercase tracking-wider">Send attempts ({r.attempt_count})</div>
        {r.attempts.length ? r.attempts.map((a) => <div key={a.no}>#{a.no} · {formatIndianDate(a.at)} · {a.ok ? `accepted by WhatsApp (${a.wa_message_id})` : `failed: ${a.error}`}</div>) : "No attempts yet."}
        <div className="mb-1 mt-3 font-semibold uppercase tracking-wider">WhatsApp status updates</div>
        {r.events?.length ? r.events.map((e, i) => <div key={i}>{formatIndianDate(e.at)} · {e.status}</div>) : "None received."}
        {r.cancel_reason && <div className="mt-3">Cancelled: {r.cancel_reason}</div>}
      </div>
    </DialogContent>
  </Dialog>
);

export const ReminderTable = ({ initialStatus = "all", refreshKey, configured, maxAttempts, onChanged, testId }) => {
  const [status, setStatus] = useState(initialStatus);
  const [type, setType] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await Reminders.list({ ...(status !== "all" && { status }), ...(type !== "all" && { type }) })); }
    catch { toast.error("Could not load reminders"); } finally { setLoading(false); }
  }, [status, type]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const act = async (r, action) => {
    setBusy(r.id + action);
    try {
      const out = await Reminders.action(r.id, action, action === "cancel" ? { reason: "Cancelled by staff" } : undefined);
      toast[out.status === "Failed" ? "error" : "success"](
        action === "cancel" ? "Reminder cancelled" : out.status === "Failed" ? `WhatsApp rejected it: ${out.last_error}` : "Submitted to WhatsApp — waiting for its confirmation");
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const columns = [
    { key: "created_at", header: "Queued on", mono: true, render: (r) => formatIndianDate(r.created_at) },
    { key: "type", header: "Reminder", render: (r) => TYPE_LABEL[r.type] },
    { key: "loan_number", header: "Loan", mono: true, render: (r) => `${r.loan_number} #${r.instalment_no}` },
    { key: "recipient_name", header: "Recipient", render: (r) => <span>{r.recipient_name}<span className="block font-mono text-[11px] text-emerald-900/50">{r.phone_masked}</span></span> },
    { key: "due_date", header: "Due", mono: true, render: (r) => formatIndianDate(r.due_date) },
    { key: "emi_paise", header: "EMI", align: "right", mono: true, render: (r) => rs(r.emi_paise) },
    { key: "status", header: "Status", render: (r) => <span><StatusBadge status={r.status} />
      {r.status === "Queued" && r.wa_message_id && <span className="block text-[10px] text-sky-800">awaiting WhatsApp confirmation</span>}
      {r.status === "Failed" && <span className="block max-w-[180px] truncate text-[10px] text-rose-700" title={r.last_error}>{r.last_error}</span>}</span> },
    { key: "attempt_count", header: "Tries", align: "right", mono: true, render: (r) => `${r.attempt_count}/${maxAttempts}` },
    { key: "_a", header: "Actions", render: (r) => {
      const fresh = !r.wa_message_id;
      return (
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => setView(r)} data-testid={`reminder-view-${r.id}`}><Eye className="h-3.5 w-3.5" /></Button>
          {r.status === "Queued" && fresh && <Button size="sm" variant="outline" className="h-8 px-2" disabled={!configured || !!busy} title={configured ? "Send now" : "WhatsApp not connected"}
            onClick={() => act(r, "send")} data-testid={`reminder-send-${r.id}`}><Send className="h-3.5 w-3.5" /></Button>}
          {r.status === "Failed" && <Button size="sm" variant="outline" className="h-8 px-2" disabled={!configured || !!busy || r.attempt_count >= maxAttempts}
            title={r.attempt_count >= maxAttempts ? "Retry limit reached" : "Retry"} onClick={() => act(r, "retry")} data-testid={`reminder-retry-${r.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>}
          {["Queued", "Failed"].includes(r.status) && fresh && <Button size="sm" variant="outline" className="h-8 border-rose-300 px-2 text-rose-700 hover:bg-rose-50" disabled={!!busy}
            onClick={() => act(r, "cancel")} data-testid={`reminder-cancel-${r.id}`}><Ban className="h-3.5 w-3.5" /></Button>}
        </div>
      );
    } },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        <div className="w-44"><Pick testId={`${testId}-status-filter`} value={status} onChange={setStatus} options={[{ value: "all", label: "All statuses" }, ...STATUSES.map((s) => ({ value: s, label: s }))]} /></div>
        <div className="w-48"><Pick testId={`${testId}-type-filter`} value={type} onChange={setType} options={[{ value: "all", label: "All reminder types" }, ...TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t] }))]} /></div>
      </div>
      {loading ? <div className="rounded-2xl border border-emerald-950/10 bg-white p-8 text-center text-sm text-emerald-900/50">Loading…</div>
        : <DataTable columns={columns} rows={rows} searchKeys={["loan_number", "recipient_name", "shg_name", "status"]} testId={testId} emptyLabel="No reminders here yet." />}
      {view && <Details r={view} onClose={() => setView(null)} />}
    </div>
  );
};
